import React from "react";
import styles from "../SubjectSinglePage.module.css";
import type {
  CreateGistTarget,
  GistItem,
  GistReaderMode,
  GistTreeNode,
  SubjectItem,
  VisibleSubjectRow,
} from "../model/types";
import {
  compactNumber,
  countGistMetrics,
  formatDate,
  getAuthorDetails,
  getFullReaderText,
  getGistDepth,
  getReplyGists,
  getVoteMetrics,
  makeReaderParagraphs,
} from "../model/subjectSingleModel";

type GistExplorerPageProps = {
  selectedSubject: SubjectItem | null;
  selectedGist: GistItem | null;
  branchGists: GistItem[];
  gistTree: GistTreeNode[];
  visibleSubjectRows: VisibleSubjectRow[];
  subjectChildrenById: Record<string, SubjectItem[]>;
  effectiveSubjectExpandedIds: Set<number>;
  subjectSearchMatchingIds: Set<number>;
  gistExplorerColumnWeights: number[];
  gistExplorerGridRef: React.RefObject<HTMLDivElement>;
  rowButtonRefs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
  isRegisteredUser: boolean;
  renderSubjectSearch: (label: string) => React.ReactNode;
  renderSubjectSearchEmpty: () => React.ReactNode;
  renderHighlightedSubjectTitle: (title: string) => React.ReactNode;
  onBackToSubjects: () => void;
  onChooseSubject: (subjectId: number) => void;
  onCreateGist: (target: CreateGistTarget) => void;
  onCreateLinkedDraft: (gist: GistItem) => void;
  onToggleSubjectExpanded: (subjectId: number) => void;
  onFocusSubject: (subjectId: number) => void;
  onSubjectClick: (
    event: React.MouseEvent,
    subject: SubjectItem,
    isExplorer: boolean,
  ) => void;
  onSubjectDoubleClick: (event: React.MouseEvent, subject: SubjectItem) => void;
  onClearPendingSubjectClick: () => void;
  onShowSubjectActionMenu: (subject: SubjectItem, x: number, y: number) => void;
  onSelectGist: (gistId: number) => void;
  onOpenGistReader: (gist: GistItem, mode?: GistReaderMode) => void;
  onStartResize: (
    dividerIndex: 0 | 1,
    event: React.PointerEvent<HTMLButtonElement>,
  ) => void;
  onResetColumns: () => void;
  onSubjectTreeKeyDown: (event: React.KeyboardEvent) => void;
};

function isDraftGist(gist: GistItem): boolean {
  return gist.status?.toUpperCase() === "DRAFT";
}

function getAuthorInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase() || "G"
  );
}

function findGistPathIds(
  nodes: GistTreeNode[],
  targetId: number,
  trail: number[] = [],
): number[] {
  for (const node of nodes) {
    const currentTrail = [...trail, node.gist.id];
    if (node.gist.id === targetId) return currentTrail;
    const childTrail = findGistPathIds(node.children, targetId, currentTrail);
    if (childTrail.length) return childTrail;
  }
  return [];
}

function findGistNode(
  nodes: GistTreeNode[],
  targetId: number,
): GistTreeNode | null {
  for (const node of nodes) {
    if (node.gist.id === targetId) return node;
    const childNode = findGistNode(node.children, targetId);
    if (childNode) return childNode;
  }
  return null;
}

function collectExpandableGistIds(
  nodes: GistTreeNode[],
  ids = new Set<number>(),
): Set<number> {
  nodes.forEach((node) => {
    if (node.children.length) ids.add(node.gist.id);
    collectExpandableGistIds(node.children, ids);
  });
  return ids;
}

function collectGistDescendantIds(node: GistTreeNode | null): number[] {
  if (!node) return [];
  return node.children.flatMap((child) => [
    child.gist.id,
    ...collectGistDescendantIds(child),
  ]);
}

function setsHaveSameIds(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  return Array.from(a).every((value) => b.has(value));
}

function GistTreeRows({
  nodes,
  selectedGistId,
  expandedGistIds,
  onSelectGist,
  onToggleGistExpanded,
}: {
  nodes: GistTreeNode[];
  selectedGistId: number | null;
  expandedGistIds: Set<number>;
  onSelectGist: (gistId: number) => void;
  onToggleGistExpanded: (gistId: number) => void;
}) {
  const renderNode = (node: GistTreeNode, depth = 0): React.ReactNode => {
    const isSelected = selectedGistId === node.gist.id;
    const metrics = countGistMetrics(node.gist);
    const isDraft = isDraftGist(node.gist);
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedGistIds.has(node.gist.id);

    return (
      <React.Fragment key={`gist-tree-${node.gist.id}`}>
        <div
          className={`${styles.gistTreeRow} ${isSelected ? styles.gistTreeRowActive : ""}`}
          style={{ "--depth": depth } as React.CSSProperties}
        >
          <button
            type="button"
            className={styles.gistTreeToggle}
            disabled={!hasChildren}
            aria-label={
              hasChildren
                ? isExpanded
                  ? `Collapse replies under ${node.gist.title}`
                  : `Expand replies under ${node.gist.title}`
                : `${node.gist.title} has no reply gists`
            }
            aria-expanded={hasChildren ? isExpanded : undefined}
            onClick={(event) => {
              event.stopPropagation();
              if (hasChildren) onToggleGistExpanded(node.gist.id);
            }}
          >
            {hasChildren ? (isExpanded ? "⌄" : "›") : ""}
          </button>
          {depth > 0 && (
            <span className={styles.gistTreeConnector} aria-hidden="true" />
          )}
          <button
            type="button"
            className={styles.gistTreeButton}
            onClick={() => onSelectGist(node.gist.id)}
            aria-pressed={isSelected}
          >
            <span className={styles.fileIcon} aria-hidden="true" />
            <span className={styles.gistTreeTitle}>{node.gist.title}</span>
            {isDraft && (
              <span className={styles.gistTreeDraftBadge}>Draft</span>
            )}
          </button>
          <span className={styles.gistTreeMeta}>
            {metrics.commentCount} replies
          </span>
        </div>
        {hasChildren && isExpanded &&
          node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return <>{nodes.map((node) => renderNode(node))}</>;
}

function SelectedGistPreview({
  selected,
  branchGists,
  subjectTitle,
  onOpenGistReader,
}: {
  selected: GistItem | null;
  branchGists: GistItem[];
  subjectTitle: string;
  onOpenGistReader: (gist: GistItem, mode?: GistReaderMode) => void;
}) {
  const metrics = selected ? countGistMetrics(selected) : null;
  const votes = selected ? getVoteMetrics(selected) : null;
  const replies = selected ? getReplyGists(selected, branchGists).length : 0;
  const author = selected ? getAuthorDetails(selected) : null;

  if (!selected || !metrics || !votes || !author) {
    return (
      <div className={styles.gistPreviewEmpty}>
        <strong>Select a gist</strong>
        <span>Click a file in the Gist tree to preview it here.</span>
      </div>
    );
  }

  const isDraft = isDraftGist(selected);

  return (
    <article className={styles.gistPreviewCard}>
      <div className={styles.gistPreviewHeader}>
        <span className={styles.selectedGistBadge}>Selected gist</span>
        <button
          type="button"
          className={styles.gistPreviewOpenButton}
          onClick={() => onOpenGistReader(selected, "read")}
        >
          {isDraft ? "Open draft ↗" : "Open gist ↗"}
        </button>
      </div>
      <h2>{selected.title}</h2>
      <div className={styles.gistPreviewAuthor}>
        <span>{getAuthorInitials(author.name)}</span>
        <div>
          <strong>{author.name}</strong>
          <small>
            {formatDate(author.date)} •{" "}
            {Math.max(
              2,
              makeReaderParagraphs(getFullReaderText(selected), 6).length * 2,
            )}{" "}
            min read
          </small>
        </div>
      </div>
      <div className={styles.gistPreviewText}>
        {makeReaderParagraphs(getFullReaderText(selected), 2).map(
          (paragraph, index) => (
            <p key={`gist-preview-${selected.id}-${index}`}>{paragraph}</p>
          ),
        )}
      </div>
      <div className={styles.gistPreviewTags}>
        <span>◇ {subjectTitle}</span>
        <span>
          # {isDraft ? "draft" : getGistDepth(selected.id, branchGists) ? "reply" : "root"}
        </span>
        <span>▥ Explainer</span>
      </div>
      <dl className={styles.gistPreviewStats}>
        <div>
          <dt>{metrics.commentCount}</dt>
          <dd>Replies</dd>
        </div>
        <div>
          <dt>{replies}</dt>
          <dd>Replied to</dd>
        </div>
        <div>
          <dt>{votes.total || 0}</dt>
          <dd>Votes</dd>
        </div>
        <div>
          <dt>
            {compactNumber(
              Math.max(1, metrics.editCount + metrics.commentCount + replies) *
                39,
            )}
          </dt>
          <dd>Views</dd>
        </div>
      </dl>
    </article>
  );
}

export const GistExplorerPage: React.FC<GistExplorerPageProps> = ({
  selectedSubject,
  selectedGist,
  branchGists,
  gistTree,
  visibleSubjectRows,
  subjectChildrenById,
  effectiveSubjectExpandedIds,
  subjectSearchMatchingIds,
  gistExplorerColumnWeights,
  gistExplorerGridRef,
  rowButtonRefs,
  isRegisteredUser,
  renderSubjectSearch,
  renderSubjectSearchEmpty,
  renderHighlightedSubjectTitle,
  onBackToSubjects,
  onChooseSubject,
  onCreateGist,
  onCreateLinkedDraft,
  onToggleSubjectExpanded,
  onFocusSubject,
  onSubjectClick,
  onSubjectDoubleClick,
  onClearPendingSubjectClick,
  onShowSubjectActionMenu,
  onSelectGist,
  onOpenGistReader,
  onStartResize,
  onResetColumns,
  onSubjectTreeKeyDown,
}) => {
  const subjectTitle = selectedSubject?.title || "Subject";
  const selectedGistId = selectedGist?.id ?? null;
  const selectedGistPath = React.useMemo(
    () =>
      typeof selectedGistId === "number"
        ? findGistPathIds(gistTree, selectedGistId)
        : [],
    [gistTree, selectedGistId],
  );
  const allExpandableGistIds = React.useMemo(
    () => collectExpandableGistIds(gistTree),
    [gistTree],
  );
  const [focusedExpandedGistIds, setFocusedExpandedGistIds] =
    React.useState<Set<number>>(() => new Set(selectedGistPath));
  const [isGistTreeExpandedAll, setIsGistTreeExpandedAll] =
    React.useState(false);

  React.useEffect(() => {
    if (isGistTreeExpandedAll) return;
    const nextExpanded = new Set(selectedGistPath);
    setFocusedExpandedGistIds((current) =>
      setsHaveSameIds(current, nextExpanded) ? current : nextExpanded,
    );
  }, [isGistTreeExpandedAll, selectedGistPath]);

  const effectiveGistExpandedIds = isGistTreeExpandedAll
    ? allExpandableGistIds
    : focusedExpandedGistIds;

  const handleToggleGistExpanded = React.useCallback(
    (gistId: number) => {
      setIsGistTreeExpandedAll(false);
      setFocusedExpandedGistIds((current) => {
        if (current.has(gistId)) {
          const next = new Set(current);
          next.delete(gistId);
          collectGistDescendantIds(findGistNode(gistTree, gistId)).forEach((id) =>
            next.delete(id),
          );
          return next;
        }

        return new Set(findGistPathIds(gistTree, gistId));
      });
    },
    [gistTree],
  );

  const focusSelectedGistBranch = React.useCallback(() => {
    setIsGistTreeExpandedAll(false);
    setFocusedExpandedGistIds(new Set(selectedGistPath));
  }, [selectedGistPath]);

  const collapseGistTree = React.useCallback(() => {
    setIsGistTreeExpandedAll(false);
    setFocusedExpandedGistIds(new Set(selectedGistPath.slice(0, -1)));
  }, [selectedGistPath]);

  const expandAllGistTree = React.useCallback(() => {
    setIsGistTreeExpandedAll(true);
  }, []);

  const renderExplorerSubjectRow = (
    subject: SubjectItem,
    depth: number,
  ): React.ReactNode => {
    const children = subjectChildrenById[String(subject.id)] || [];
    const hasChildren = children.length > 0;
    const isExpanded = effectiveSubjectExpandedIds.has(subject.id);
    const isSelected = selectedSubject?.id === subject.id;
    const isSearchMatch = subjectSearchMatchingIds.has(subject.id);

    return (
      <div
        key={`explorer-subject-${subject.id}`}
        className={`${styles.explorerTreeRow} ${isSelected ? styles.explorerTreeRowActive : ""} ${isSearchMatch ? styles.explorerTreeRowSearchMatch : ""}`}
        style={{ "--depth": depth } as React.CSSProperties}
        role="treeitem"
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
      >
        <button
          type="button"
          className={styles.explorerToggle}
          aria-label={
            hasChildren
              ? isExpanded
                ? `Collapse ${subject.title}`
                : `Expand ${subject.title}`
              : `${subject.title} has no children`
          }
          disabled={!hasChildren}
          onClick={(event) => {
            event.stopPropagation();
            if (hasChildren) onToggleSubjectExpanded(subject.id);
          }}
        >
          {hasChildren ? (isExpanded ? "⌄" : "›") : ""}
        </button>
        {depth > 0 && (
          <span className={styles.explorerConnector} aria-hidden="true" />
        )}
        <button
          type="button"
          ref={(node) => {
            rowButtonRefs.current[subject.id] = node;
          }}
          className={styles.explorerTreeButton}
          aria-label={`${subject.title}. Press Enter to view gists, or double click for more actions.`}
          onFocus={() => onFocusSubject(subject.id)}
          onClick={(event) => onSubjectClick(event, subject, true)}
          onDoubleClick={(event) => onSubjectDoubleClick(event, subject)}
        >
          <span className={styles.folderIcon} aria-hidden="true" />
          <span>{renderHighlightedSubjectTitle(subject.title)}</span>
        </button>
        <button
          type="button"
          className={styles.rowActionButton}
          aria-label={`Open actions for ${subject.title}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClearPendingSubjectClick();
            onShowSubjectActionMenu(subject, event.clientX, event.clientY);
          }}
        >
          ⋯
        </button>
      </div>
    );
  };

  return (
    <section
      className={styles.gistExplorerPanel}
      aria-labelledby="gist-explorer-title"
    >
      <div className={styles.gistExplorerHeader}>
        <div className={styles.gistExplorerTitleBlock}>
          <button
            type="button"
            className={styles.gistExplorerBack}
            onClick={onBackToSubjects}
            aria-label="Back to subjects"
          >
            ‹
          </button>
          <span className={styles.gistExplorerFolder} aria-hidden="true" />
          <div>
            <h1 id="gist-explorer-title">{subjectTitle}</h1>
            <p>Explore direct gists inside this subject. Child-subject gists stay in their own folders.</p>
          </div>
        </div>

        <div className={styles.gistExplorerActions}>
          <nav aria-label="Gist explorer breadcrumb">
            <button type="button" onClick={onBackToSubjects}>
              Subjects
            </button>
            <span>/</span>
            <button
              type="button"
              onClick={() => selectedSubject && onChooseSubject(selectedSubject.id)}
            >
              {subjectTitle}
            </button>
            <span>/</span>
            <b>Gists</b>
          </nav>
          <button
            type="button"
            className={styles.addGistButton}
            disabled={!selectedSubject || !isRegisteredUser}
            title={
              !isRegisteredUser
                ? "Login required"
                : selectedGist
                  ? `Create a reply gist to ${selectedGist.title}`
                  : `Create a gist in ${subjectTitle}`
            }
            onClick={() => {
              if (!selectedSubject) return;
              onCreateGist({
                subjectId: selectedSubject.id,
                ...(selectedGist ? { parentGistId: selectedGist.id } : {}),
              });
            }}
          >
            {selectedGist ? "+ Reply gist" : "+ Add gist"}
          </button>
          <button
            type="button"
            className={styles.addGistButton}
            disabled={!selectedGist || !isRegisteredUser}
            title={
              !isRegisteredUser
                ? "Login required"
                : selectedGist
                  ? `Create a private linked draft under ${selectedGist.title}`
                  : "Select a gist first"
            }
            onClick={() => selectedGist && onCreateLinkedDraft(selectedGist)}
          >
            + Linked draft
          </button>
          <button
            type="button"
            className={styles.menuIconButton}
            aria-label="More gist tree options"
          >
            ☰
          </button>
        </div>
      </div>

      <div
        ref={gistExplorerGridRef}
        className={styles.gistExplorerGrid}
        style={
          {
            "--subject-column-size": `${gistExplorerColumnWeights[0]}fr`,
            "--gist-column-size": `${gistExplorerColumnWeights[1]}fr`,
            "--preview-column-size": `${gistExplorerColumnWeights[2]}fr`,
          } as React.CSSProperties
        }
      >
        <aside className={styles.subjectExplorerColumn} aria-label="Subject tree">
          <div className={styles.explorerColumnHeader}>
            <span>Subject tree</span>
          </div>
          {renderSubjectSearch("Search subjects in explorer tree")}
          <div
            className={styles.explorerSubjectTree}
            role="tree"
            aria-label="Explorer subject tree"
            onKeyDown={onSubjectTreeKeyDown}
          >
            {visibleSubjectRows.length
              ? visibleSubjectRows.map((row) =>
                  renderExplorerSubjectRow(row.subject, row.depth),
                )
              : renderSubjectSearchEmpty()}
          </div>
        </aside>

        <button
          type="button"
          className={styles.gistExplorerResizeHandle}
          aria-label="Resize Subject tree and Gist tree columns"
          title="Drag to resize columns. Double-click to reset."
          onPointerDown={(event) => onStartResize(0, event)}
          onDoubleClick={onResetColumns}
        >
          <span aria-hidden="true" />
        </button>

        <section className={styles.gistTreeColumn} aria-label="Gist tree">
          <div className={styles.explorerColumnHeader}>
            <span>Gist tree</span>
            <div className={styles.gistTreeHeaderActions}>
              <button
                type="button"
                className={styles.gistTreeHeaderButton}
                onClick={focusSelectedGistBranch}
                disabled={!selectedGist}
              >
                Focus
              </button>
              <button
                type="button"
                className={styles.gistTreeHeaderButton}
                onClick={expandAllGistTree}
                disabled={!gistTree.length}
              >
                Expand all
              </button>
              <button
                type="button"
                className={styles.gistTreeHeaderButton}
                onClick={collapseGistTree}
                disabled={!gistTree.length}
              >
                Collapse
              </button>
            </div>
          </div>
          <div className={styles.gistTreeList}>
            {gistTree.length ? (
              <GistTreeRows
                nodes={gistTree}
                selectedGistId={selectedGist?.id ?? null}
                expandedGistIds={effectiveGistExpandedIds}
                onSelectGist={onSelectGist}
                onToggleGistExpanded={handleToggleGistExpanded}
              />
            ) : (
              <div className={styles.gistTreeEmpty}>
                <strong>No direct gists in this subject yet.</strong>
                <span>Move into a child subject, or create a direct gist here.</span>
              </div>
            )}
          </div>
        </section>

        <button
          type="button"
          className={styles.gistExplorerResizeHandle}
          aria-label="Resize Gist tree and Selected gist columns"
          title="Drag to resize columns. Double-click to reset."
          onPointerDown={(event) => onStartResize(1, event)}
          onDoubleClick={onResetColumns}
        >
          <span aria-hidden="true" />
        </button>

        <aside className={styles.gistPreviewColumn} aria-label="Selected gist preview">
          <SelectedGistPreview
            selected={selectedGist}
            branchGists={branchGists}
            subjectTitle={subjectTitle}
            onOpenGistReader={onOpenGistReader}
          />
        </aside>
      </div>
    </section>
  );
};
